<template>
  <core-main class="px-8 py-12">
    <!-- Header Section -->
    <template #toolbar-title>
      {{ $t('form.myParts.name') }}
    </template>

    <template #toolbar-icons>
      <v-btn
        slot="activator"
        icon
        @click="setView"
      >
        <v-icon>{{ getDisplayIcon }}</v-icon>
      </v-btn>

      <v-btn
        slot="activator"
        icon
        @click="toggleFilter"
      >
        <v-icon>
          mdi-filter
        </v-icon>
      </v-btn>
    </template>

    <template #extension>
      <v-row>
        <v-col>
          <core-search-bar v-model="search" />
        </v-col>
      </v-row>
    </template>

    <core-no-content v-if="_.isEmpty(getparts)" />

    <v-row
      v-else
      class="fill-height"
    >
      <v-col>
        <v-row v-if="!cardView">
          <v-col>
            <core-main-table
              :headers="headers"
              :items="getparts"
              :search.sync="search"
              rights-group="rfq"
            >
              <template v-slot:item.clientPartNo="{ item }">
                <div class="d-flex">
                  <v-img
                    :src="getPartName"
                    max-width="20px"
                    max-height="20px"
                    class="mr-2"
                  />
                  <span>{{ item.clientPartNo }}</span>
                </div>
              </template>

              <template v-slot:item.updatedAt="{ item }">
                <span>
                  {{ formatDate(item.updatedAt) }}
                </span>
              </template>

              <template v-slot:item.pricing="props">
                <core-table-unit-pricing
                  :item="props.item"
                />
              </template>

              <template v-slot:item.thickness="{ item }">
                <span>
                  {{ item.thickness }} {{ item.thickness ? 'mm' : '' }}
                </span>
              </template>
            </core-main-table>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col v-for="(item, index) of searchedForParts" :key="index" cols="3">
            <core-part-card
              :part="item"
              @part:click="onOpenDetail($event)"
            />
            <!-- @part:click="togglePartViewer($event)" -->
          </v-col>
        </v-row>
      </v-col>

      <v-col
        v-if="areFiltersActive"
        cols="2"
        class="pt-0 fill-height"
        style="border-left: 1px solid rgba(0,0,0,.12);"
      >
        <core-filter-main
          v-model="partsFilterData"
          @filter:close="areFiltersActive = false"
        >
          <core-filter-checkbox-group
            v-model="filterData.material.value"
            :options="filterData.material.options"
            :section="filterData.material.section"
          />
          <v-divider class="mt-6 ml-4" />

          <core-filter-checkbox-group
            v-model="filterData.finish.value"
            :options="filterData.finish.options"
            :section="filterData.finish.section"
          />
          <v-divider class="mt-6 ml-4" />

          <core-filter-radio-group
            v-model="filterData.orderBy.value"
            :options="filterData.orderBy.options"
            :section="filterData.orderBy.section"
          />
          <v-divider class="ml-4" />

          <core-filter-range-slider
            v-model="filterData.thickness.value"
            :options="filterData.thickness.options"
            :section="filterData.thickness.section"
          />
          <v-divider class="ml-4" />

          <core-filter-switch-group
            v-model="filterData.ownership.value"
            :options="filterData.ownership.options"
            :section="filterData.ownership.section"
          />
        </core-filter-main>
      </v-col>
    </v-row>

    <v-navigation-drawer
      v-model="isPartViewerActive"
      width="30%"
      style="margin-top: 64px"
      fixed
      clipped
      right
      temporary
    >
      <div class="d-flex justify-end mr-2 mt-2">
        <v-btn
          icon
          @click="togglePartViewer(false)"
        >
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>
      </div>
      <div class="d-flex justify-center my-2">
        <div
          v-if="partViewerPart"
          id="3dview"
        />
        <!-- <v-img
          v-if="partViewerPart"
          :src="getPartName"
          max-width="70%"
        /> -->
      </div>
      <v-row class="px-8">
        <v-col>
          <v-tabs
            v-model="partViewerActiveTab"
            grow
            centered
            class="pt-5"
          >
            <v-tab v-for="(item, i) in itemViewerTabItems" :key="i">
              <div class="d-flex flex-column">
                <v-icon>
                  {{ item.icon }}
                </v-icon>
                <span>
                  {{ item.text }}
                </span>
              </div>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="partViewerActiveTab">
            <v-tab-item />
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-navigation-drawer>
  </core-main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { OBJLoader2 } from 'three/examples/jsm/loaders/OBJLoader2';
import CurrencyFormatterMixin from '../../mixins/currencyFormatter';
import FilterableTextMixin from '../mixins/filterableText';

// Consts
// The scene should be a const to be able to reach it inside a callback function
const scene = new THREE.Scene();

export default {

  mixins: [
    CurrencyFormatterMixin,
    FilterableTextMixin,
  ],

  data() {
    return {
      fullScreen: false,
      width3d: 500,
      height3d: 300,
      cameraTarget: null,

      search: '',
      areFiltersActive: false,
      isPartViewerActive: false,
      partViewerActiveTab: 0,
      partViewerPart: {},
      cardView: true,
      filterData: {
        material: {
          section: 'Material',
          options: [
            'Stainless', 'Aluminum', 'Steel', 'Spring Steel', 'Gold Galvanized Steel'
          ],
          value: [
          ],
        },
        finish: {
          section: 'Finish',
          options: [
            'None', 'ClearZinc', 'Protective Oil', 'Ecoating', 'YellowZinc'
          ],
          value: [
          ],
        },
        orderBy: {
          section: 'Order By',
          options: [
            'Description', 'Part Number', 'Material', 'Finish'
          ],
          value: '',
        },
        thickness: {
          section: 'Thickness',
          min: 0,
          max: 500,
          value: [
            0, 0
          ],
        },
        ownership: {
          section: 'Ownership',
          options: [
            'Submitted by Me',
          ],
          value: [
          ],
        },
      },
    };
  },

  computed: {
    ...mapGetters('parts', { findPartsFromStore: 'find' }),

    ...mapGetters('rfqs', { findRfqsFromStore: 'find' }),

    getRfqs() {
      return this.findRfqsFromStore({ query: {} }).data;
    },

    getparts() {
      return this.findPartsFromStore({ query: {} }).data;
      // const allParts = _.map(this.getRfqs, rfq => rfq.parts);
      // return _.uniq(_.flatten(allParts));
    },

    searchedForParts() {
      return this.filterItems(this.getparts, this.search);
    },

    getPartName() {
      return `/img/placeholder_item_${Math.floor(Math.random() * 3) + 1}.png`;
    },

    getDisplayIcon() {
      return this.cardView ? 'mdi-format-list-bulleted' : 'mdi-view-grid';
    },

    itemViewerTabItems() {
      return [
        { text: 'Specs', icon: 'mdi-receipt' },
        { text: 'Analysis', icon: 'mdi-bug' },
        { text: 'Revisions', icon: 'mdi-refresh' },
      ];
    },

    headers() {
      return [
        {
          text: 'Client part #',
          value: 'clientPartNo',
        },
        {
          text: 'Description',
          value: 'description',
        },
        {
          text: 'Komacut Part #',
          value: 'manufacturerPartNo',
        },
        {
          text: 'File Name',
          value: 'fileName',
        },
        {
          text: 'Material',
          value: 'material',
        },
        {
          text: 'Finish',
          value: 'finish',
        },
        {
          text: 'Thickness',
          value: 'thickness',
        },
        {
          text: 'Price/MOQ',
          value: 'pricing',
        },
        {
          text: 'Updated on',
          value: 'updatedAt',
        },
        {
          text: '',
          value: 'data-table-menu',
          items: [
          ]
        },
      ];
    },
  },

  mounted() {
    this.findRfqsFromBackend({ query: {} });
    this.findPartsFromBackend({ query: { EstimateID: 'ALL' } });
    this.init();
    // window.addEventListener('mousemove', e => {
    //   // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
    // });
  },

  created() {
    window.addEventListener('resize', this.onResize);
  },

  destroyed() {
    window.removeEventListener('resize', this.onResize);
    // Removing all children...
    for (let i = scene.children.length - 1; i >= 0; i--) {
      scene.remove(scene.children[i]);
    }

    let myNode = document.getElementById('3dview');
    if (myNode) {
      while (myNode.firstChild) {
        myNode.removeChild(myNode.firstChild);
      }
    }
    myNode = document.getElementById('FullScreen3dView');
    if (myNode) {
      while (myNode.firstChild) {
        myNode.removeChild(myNode.firstChild);
      }
    }
    this.animate = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
  },

  methods: {
    ...mapActions('parts', { findPartsFromBackend: 'find' }),

    ...mapActions('rfqs', { findRfqsFromBackend: 'find' }),

    onOpenDetail(part) {
      this.isPartViewerActive = true;
      this.partViewerPart = part;
      const baseMaterial = new THREE.MeshPhongMaterial({ color: 0x777777 });

      // Removing all children...
      for (let i = scene.children.length - 1; i >= 0; i--) {
        if (
          scene.children[i].type === 'Mesh'
          || scene.children[i].type === 'LineSegments'
        ) {
          scene.remove(scene.children[i]);
        }
      }

      // Add current
      this.loader.load(
        'https://anl-dev.komacut.be:3031/uploads/C5B01-2410.obj',
        '/data/C5B01-2410.obj',
        mesh => {
          mesh.scale.set(0.5, 0.5, 0.5);
          mesh.position.set(0, 0, 0);
          mesh.rotation.set(0, 0, 0);

          mesh.traverse(child => {
            if (child instanceof THREE.Mesh) {
              child.material = baseMaterial;
              child.material.side = THREE.DoubleSide;

              if (child.geometry !== undefined) {
                const edges = new THREE.EdgesGeometry(child.geometry);
                const line = new THREE.LineSegments(
                  edges,
                  new THREE.LineBasicMaterial({ color: 0x000000 })
                );
                line.scale.set(0.5, 0.5, 0.5);
                scene.add(line);
              }
            }
          });

          scene.add(mesh);
        },
        undefined,
        error => {
          console.error(error);
        }
      );
    },

    onResize(e) {
      if (this.renderer) {
        let width = 500;
        let height = 350;
        if (this.fullScreen) {
          width = window.innerWidth - 50;
          height = window.innerHeight - 70; // Remove toolbar height
        }
        this.width3d = width;
        this.height3d = height;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.width3d, this.height3d);
      }
    },

    onFullScreen(full) {
      let dst = document.getElementById('3dview');
      let src = document.getElementById('FullScreen3dView');
      if (full) {
        dst = document.getElementById('FullScreen3dView');
        src = document.getElementById('3dview');
      }
      while (src.childNodes.length > 0) {
        dst.appendChild(src.childNodes[0]);
      }
      this.fullScreen = full;
      this.onResize();
    },

    addShadowedLight(x, y, z, color, intensity) {
      const directionalLight = new THREE.DirectionalLight(color, intensity);
      directionalLight.position.set(x, y, z);
      directionalLight.lookAt(0, 0, 0);
      this.camera.add(directionalLight);

      directionalLight.castShadow = true;

      const d = 1;
      directionalLight.shadow.camera.left = -d;
      directionalLight.shadow.camera.right = d;
      directionalLight.shadow.camera.top = d;
      directionalLight.shadow.camera.bottom = -d;

      directionalLight.shadow.camera.near = 1;
      directionalLight.shadow.camera.far = 4;

      directionalLight.shadow.mapSize.width = 1024;
      directionalLight.shadow.mapSize.height = 1024;

      directionalLight.shadow.bias = -0.002;
    },

    createLights() {
      if (this.camera) {
        // Lights
        this.camera.add(new THREE.HemisphereLight(0x443333, 0x111122));
        this.addShadowedLight(1, 1, 1, 0xffffff, 1.35);
      }
    },
    animate() {
      if (this.animate) {
        requestAnimationFrame(this.animate);
        this.controls.update();
        this.render();
      }
    },
    render() {
      if (this.renderer) {
        // https://threejs.org/examples/#webgl_geometry_cube
        // const timer = Date.now() * 0.0005;
        // this.camera.position.x = Math.cos(timer) * 100;
        // this.camera.position.z = Math.sin(timer) * 100;

        this.camera.lookAt(0, 0, 0);
        this.renderer.render(scene, this.camera);
      }
    },
    init() {
      const VIEW_ANGLE = 35;
      const ASPECT = this.width3d / this.height3d;
      const NEAR = 0.1;
      const FAR = 20000;

      const container = document.getElementById('3dview');

      // Renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(this.width3d, this.height3d);
      container.appendChild(this.renderer.domElement);

      // Scene
      scene.background = new THREE.Color(0xeeeeee);

      // Camera
      this.camera = new THREE.PerspectiveCamera(
        VIEW_ANGLE, // Field of view
        ASPECT, // Aspect ratio
        NEAR, // Near plane
        FAR // Far plane
      );
      this.cameraTarget = new THREE.Vector3(0, -0.25, 0);
      this.camera.position.set(
        -37.565773010253906,
        -25.910253524780273,
        -97.5260238647461
      );
      this.camera.lookAt(this.cameraTarget);
      scene.add(this.camera);
      // Lights
      this.createLights();

      // placeholder for the FreeCAD camera
      this.controls = new TrackballControls(this.camera, this.renderer.domElement);
      this.controls.rotateSpeed = 10;
      this.controls.zoomSpeed = 10;
      this.controls.noZoom = false;
      this.controls.noPan = true;
      this.controls.staticMoving = true;
      this.controls.dynamicDampingFactor = 0.3;
      this.controls.keys = [65, 83, 68];

      // // Load STL files
      // this.loader = new STLLoader();

      // Load OBJ files
      this.loader = new OBJLoader2();

      // Call render
      this.animate();
    },

    setView() {
      this.cardView = !this.cardView;
    },

    toggleFilter() {
      this.areFiltersActive = !this.areFiltersActive;
    },

    // togglePartViewer(part) {
    //   this.isPartViewerActive = !!part;
    //   this.partViewerPart = part;
    // },

    closePartViewer() {
      // TODO
    },

    getItemImage(item) {
      return `/img/placeholder_item_${_.indexOf(this.items, item) + 1}.png`;
    },

    formatDate(date) {
      if (!moment(date).isValid()) {
        return '';
      }
      return moment(date).format('YYYY-MM-DD');
    },
  },
};
</script>
